/* eslint-disable @typescript-eslint/no-misused-promises */
/* eslint-disable @typescript-eslint/no-floating-promises */
import DeleteIcon from "@mui/icons-material/Delete";
import { usePopup } from "@nodeon/react-components";
import { useSnackbar } from "notistack";
import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import {
  OrganizationDetailsDto,
  OrganizationRoleListDto,
  useOrganizationApi,
} from "~/api/GeneratedApi";
import GenericTable from "~/components/GenericTable";
import { useApiErrorHandler } from "~/utils/hooks/useApiErrorHandler";
import OrganizationRoleCreationDialog from "../dialogs/OrganizationRoleCreationDialog";

interface Props {
  organizationData: OrganizationDetailsDto;
}

const OrganizationRolesTable = ({ organizationData }: Props): JSX.Element => {
  const { t } = useTranslation("organization-management");

  const organizationApi = useOrganizationApi();

  const { confirm, show } = usePopup();
  const handleApiError = useApiErrorHandler();
  const { enqueueSnackbar } = useSnackbar();
  const refetchRef = useRef<() => void>();
  const history = useHistory();

  return (
    <GenericTable<OrganizationRoleListDto>
      columns={[
        {
          title: t("role-name"),
          field: "roleName",
        },
      ]}
      fetch={(opts) =>
        organizationApi.getOrganizationRoles(organizationData.id, opts)
      }
      getRefetch={(r) => {
        refetchRef.current = r;
      }}
      tableProps={{
        title: t("organizations-roles", {
          organization: organizationData.name,
        }),
        actions: [
          {
            icon: "edit",
            tooltip: t("edit-role"),
            onClick: (_, data) => {
              if (Array.isArray(data)) return;
              history.push(
                `/organizations/${organizationData.id}/role/${data.id}`,
              );
            },
          },
          {
            icon: "delete",
            tooltip: t("delete-role"),
            onClick: (_, rowData) => {
              if ("length" in rowData) return;
              confirm({
                title: t("general:are-you-sure"),
                description: t("delete-role-popup-description", {
                  role: rowData.roleName,
                }),
                icon: <DeleteIcon />,
              }).then(() => {
                organizationApi
                  .deleteOrganizationRole(organizationData.id, rowData.id)
                  .then(() => {
                    enqueueSnackbar(t("role-deleted"), { variant: "success" });
                    refetchRef.current?.();
                  })
                  .catch(handleApiError);
              });
            },
          },
          {
            icon: "add",
            tooltip: t("general:add"),
            isFreeAction: true,
            onClick: () =>
              show(
                <OrganizationRoleCreationDialog
                  organizationId={organizationData.id}
                  onSubmitted={refetchRef.current}
                />,
                { noDialog: true },
              ),
          },
        ],
      }}
    />
  );
};

export default OrganizationRolesTable;
